import { DocumentNode } from 'graphql';
import { IssueTicket } from '../../config';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
  CreateIssueCommentDocument,
  IssueTicketStatus,
  UpdateIssueTicketDocument,
} from 'src/graphql/generated/graphql';
import {
  BottomNavigation,
  Button,
  Controller,
  FormDirtyModal,
  PageModal,
  TextEditor,
  Typography,
  useForm,
} from '@otp/ui';
import { StatusField } from 'src/components/issue/tickets/form/fields/status';
import { useState } from 'react';

type Form = {
  status: IssueTicketStatus;
  comment: string;
};

type ChangeStatusModalProps = {
  issueTicket: IssueTicket;
  refetchQueries?: DocumentNode[];
  onClose: () => void;
};

export const ChangeStatusModal = ({
  issueTicket,
  refetchQueries = [],
  onClose,
}: ChangeStatusModalProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'issue.ticket.list.actions.change-status-modal',
  });
  const [displayFormDirtyModal, setDisplayFormDirtyModal] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<Form>({
    defaultValues: {
      status: issueTicket.status,
      comment: '',
    },
  });

  const [updateIssueTicket, { loading: loadingUpdateIssue }] = useMutation(
    UpdateIssueTicketDocument,
    {
      refetchQueries,
    },
  );

  const [createIssueComment, { loading: loadingCreateIssue }] = useMutation(
    CreateIssueCommentDocument,
    {
      refetchQueries,
    },
  );

  const onSubmit = async (data: Form) => {
    await updateIssueTicket({
      variables: {
        id: issueTicket.id,
        data: {
          status: data.status,
        },
      },
    });

    if (data.comment) {
      await createIssueComment({
        variables: {
          data: {
            comment: data.comment,
            issueTicketId: issueTicket.id,
          },
        },
      });
    }

    onClose();
  };

  const onCancel = () => {
    if (isDirty) {
      setDisplayFormDirtyModal(true);
    } else {
      onClose();
    }
  };
  return (
    <PageModal open onClose={onCancel} title={t('title')}>
      <div className="w-full min-w-small">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <div>
                  <Typography className="mb-2" variant="subtitle1">
                    {t('status')}
                  </Typography>
                  <StatusField field={field} hasError={!!errors.status} />
                </div>
              )}
            />
          </div>
          <div className="mb-4">
            <Controller
              name="comment"
              control={control}
              render={({ field }) => (
                <div>
                  <Typography className="mb-2" variant="subtitle1">
                    {t('add-comment')}
                  </Typography>
                  <TextEditor {...field} defaultValue={field.value} />
                </div>
              )}
            />
          </div>
          <BottomNavigation>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loadingCreateIssue || loadingUpdateIssue}
            >
              {t('change-status')}
            </Button>
            <Button variant="outlined" onClick={onCancel}>
              {t('close')}
            </Button>
          </BottomNavigation>
        </form>
      </div>
      <FormDirtyModal
        open={displayFormDirtyModal}
        onClose={() => setDisplayFormDirtyModal(false)}
        onCloseWithoutSaving={onClose}
      />
    </PageModal>
  );
};
